import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  TableContainer,
  Typography,
} from "@mui/material";
import { CheckCircleOutline, Send } from "@mui/icons-material";
import { useOktaAuth } from "@okta/okta-react";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

export const NidRename = ({ userId }) => {
  const [state, setState] = useState({ isLoaded: false, error: null, success: false, nids: null });
  const [text, setText] = useState(userId);

  const { authState } = useOktaAuth();
  const { setUserModule } = useContext(UserModuleContext);

  useEffect(() => {
    const fetchNidList = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/nid/list`,
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: authState.accessToken.accessToken,
            },
            body: JSON.stringify({ networkId: userId }),
          }
        );

        const result = await res.json();
        setState((prev) => ({ ...prev, nids: result.nidList, isLoaded: true }));
        setText(result.nidList[0]);
      } catch (error) {
        setState((prev) => ({ ...prev, isLoaded: true, error }));
      }
    };

    fetchNidList();
  }, [authState.accessToken.accessToken, userId]);

  const updateNid = async () => {
    setState((prev) => ({ ...prev, isLoaded: false, error: null, success: false }));

    try {
      const res = await fetch(
        `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/nid/rename`,
        {
          method: "POST",
          signal: AbortSignal.timeout(360000),
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: authState.accessToken.accessToken,
          },
          body: JSON.stringify({ userId, newId: text, callerId: authState.accessToken.claims.sub }),
        }
      );

      if (res.status === 200) {
        setUserModule((prev) => ({ ...prev, userId: text }));
        setState((prev) => ({ ...prev, isLoaded: true, success: true }));
      } else {
        setState((prev) => ({ ...prev, isLoaded: true, error: { message: "Failed to rename NID" } }));
      }
    } catch (error) {
      setState((prev) => ({ ...prev, isLoaded: true, error }));
    }
  };

  if (!state.isLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  if (state.error) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Rename failed</Typography>
          <Typography variant="body2">{state.error.message}</Typography>
        </Box>
      </TableContainer>
    );
  }

  if (state.success) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: "#2e7d32", color: "white", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <CheckCircleOutline fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>NID Renamed</Typography>
        </Box>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Confirmation
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            NID/FID {userId} has been renamed to {text}
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

      {/* ── Section: NID Options ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          NID Options
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{userId}</Typography>
      </Box>

      <Divider sx={{ mx: 3 }} />

      <Box sx={{ px: 3, py: 2 }}>
        <RadioGroup
          defaultValue={state.nids[0]}
          onChange={(e) => setText(e.target.value)}
        >
          {state.nids.map((nid) => (
            <FormControlLabel
              key={`${nid}-option`}
              value={nid}
              control={<Radio sx={{ color: WSU_RED, "&.Mui-checked": { color: WSU_RED } }} />}
              label={nid}
            />
          ))}
        </RadioGroup>
      </Box>

      {/* ── Actions ── */}
      <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          endIcon={<Send />}
          onClick={updateNid}
          sx={{
            backgroundColor: WSU_RED,
            color: "white",
            px: 4,
            py: 1.25,
            fontWeight: 600,
            letterSpacing: 0.5,
            "&:hover": { backgroundColor: "#8a0c25" },
          }}
        >
          Update NID/FID
        </Button>
      </Box>

    </TableContainer>
  );
};
